import type { ComponentType } from 'react'
import { Stamp } from 'lucide-react'
import { useCurrentUser } from '@/hooks/use-auth'
import { useTaskBadges } from '@/hooks/use-task-badges'
import { useApprovalTasks } from '@/hooks/use-approval-tasks'
import { useViewRoleStore } from '@/stores/view-role'
import { TASK_NODES } from '@/lib/task-nodes'

export interface PendingTaskItem {
  key: string
  label: string
  count: number
  href: string
  icon: ComponentType<{ size?: number }>
  /** true=审批流(待我审批),false=业务链路任务节点 */
  approval?: boolean
}

/**
 * 当前用户的「待办任务」:按角色过滤、计数>0 的业务链路任务节点 + 审批流待办。
 * 供通知铃铛「待办任务」栏、通知中心「待办任务」Tab 共用。
 */
export function usePendingTasks(): { items: PendingTaskItem[]; total: number; error: Error | null } {
  const { data: user } = useCurrentUser()
  const actual = (user?.roles as string[] | undefined) || []
  const viewRole = useViewRoleStore(state => state.viewRole)
  const roles = actual.includes('ADMIN') && viewRole ? [viewRole] : actual
  const { data: badges = {}, error } = useTaskBadges()
  const { data: approvals = [] } = useApprovalTasks()

  const nodes: PendingTaskItem[] = TASK_NODES
    .filter((n) => n.roles.some((r) => roles.includes(r)))
    .map((n) => ({ key: n.key, label: n.label, count: badges[n.badgeKey] || 0, href: n.href, icon: n.icon }))
    .filter((n) => n.count > 0)

  const items = [...nodes]
  const represented = new Set(TASK_NODES.map(node => node.approvalBizType).filter(Boolean))
  const extra = [...new Map(approvals.filter(task => !represented.has(task.bizType) && (roles.includes('ADMIN') || roles.includes(task.assigneeRole))).map(task => [task.taskId, task])).values()]
  if (extra.length > 0) {
    items.push({ key: 'approval', label: '待我审批', count: extra.length, href: '/dashboard', icon: Stamp, approval: true })
  }

  return { error, items, total: items.reduce((s, i) => s + i.count, 0) }
}
